import createClient, { type Client, type MaybeOptionalInit, type Middleware } from 'openapi-fetch'
import type { HttpMethod, PathsWithMethod, RequiredKeysOf } from 'openapi-typescript-helpers'
import { HttpError, isAbortError, unwrap, unwrapResponse } from './request'

/** Sends one request. The default reads the global `fetch` when the request goes out. */
export type SendRequest = (request: Request) => Promise<Response>

export type ApiClientConfig = {
    /** A path such as `/api` resolves against the page origin in a browser. */
    baseUrl: string
    /** Resolves the bearer token of one request. A caller behind a proxy that attaches the token omits it. */
    getToken?: (request: Request) => Promise<string | undefined>
    /** Runs after the auth middleware, in order. */
    middleware?: Middleware[]
    send?: SendRequest
}

/*
 * `fetch` is read when the request is sent, not when the client is created. A test server that
 * patches the global after the client module loads still sees every request.
 */
const sendWithGlobalFetch: SendRequest = (request) => fetch(request)

// openapi-fetch builds a `Request`, which needs an absolute URL inside a browser page.
const resolveBaseUrl = (baseUrl: string): string =>
    typeof window === 'undefined' || !baseUrl.startsWith('/') ? baseUrl : new URL(baseUrl, window.location.origin).href

/*
 * A blank id still interpolates into a valid-looking URL, which reaches the wrong resource. The
 * request stops here instead, for every operation.
 */
const rejectBlankPathParams: Middleware = {
    onRequest({ params, schemaPath }) {
        const blankParam = Object.entries(params.path ?? {}).find(
            // eslint-disable-next-line typescript/no-base-to-string -- a path parameter blanks the way String() prints it, whatever its type
            ([, value]) => value === undefined || value === null || String(value).trim() === ''
        )
        if (blankParam) throw new Error(`The path parameter ${blankParam[0]} of ${schemaPath} is blank`)
    }
}

/*
 * A request that never reaches the API rejects with the `TypeError` of fetch. It becomes an
 * `HttpError`, so a caller handles an `HttpError` or an abort and nothing else.
 */
const toNetworkError: Middleware = {
    onError({ error }) {
        if (isAbortError(error)) return undefined

        return new HttpError({
            status: 0,
            code: 'NETWORK_ERROR',
            message: error instanceof Error ? error.message : undefined
        })
    }
}

const createAuthMiddleware = (getToken: NonNullable<ApiClientConfig['getToken']>): Middleware => ({
    async onRequest({ request }) {
        const token = await getToken(request)
        if (token) request.headers.set('Authorization', `Bearer ${token}`)
        return request
    }
})

// A generated `paths` interface has no index signature. It fits once `WithOverrides` maps it.
export type ApiPaths = Record<string, Partial<Record<HttpMethod, unknown>>>

// openapi-fetch keeps this helper internal. It makes `init` required when the operation has a required parameter.
export type InitParam<Init> =
    RequiredKeysOf<Init> extends never ? [(Init & { [key: string]: unknown })?] : [Init & { [key: string]: unknown }]

const withThrowingMethods = <Paths extends ApiPaths>(client: Client<Paths>) => ({
    GET: <Path extends PathsWithMethod<Paths, 'get'>, Init extends MaybeOptionalInit<Paths[Path], 'get'>>(
        url: Path,
        ...init: InitParam<Init>
    ) => unwrap(client.GET<Path, Init>(url, ...init)),
    /** `GET` that also resolves the response, for a caller that reads its headers (a file name, a page). */
    GETWithResponse: <Path extends PathsWithMethod<Paths, 'get'>, Init extends MaybeOptionalInit<Paths[Path], 'get'>>(
        url: Path,
        ...init: InitParam<Init>
    ) => unwrapResponse(client.GET<Path, Init>(url, ...init)),
    POST: <Path extends PathsWithMethod<Paths, 'post'>, Init extends MaybeOptionalInit<Paths[Path], 'post'>>(
        url: Path,
        ...init: InitParam<Init>
    ) => unwrap(client.POST<Path, Init>(url, ...init)),
    PUT: <Path extends PathsWithMethod<Paths, 'put'>, Init extends MaybeOptionalInit<Paths[Path], 'put'>>(
        url: Path,
        ...init: InitParam<Init>
    ) => unwrap(client.PUT<Path, Init>(url, ...init)),
    PATCH: <Path extends PathsWithMethod<Paths, 'patch'>, Init extends MaybeOptionalInit<Paths[Path], 'patch'>>(
        url: Path,
        ...init: InitParam<Init>
    ) => unwrap(client.PATCH<Path, Init>(url, ...init)),
    DELETE: <Path extends PathsWithMethod<Paths, 'delete'>, Init extends MaybeOptionalInit<Paths[Path], 'delete'>>(
        url: Path,
        ...init: InitParam<Init>
    ) => unwrap(client.DELETE<Path, Init>(url, ...init))
})

export type ThrowingMethods<Paths extends ApiPaths> = ReturnType<typeof withThrowingMethods<Paths>>

/**
 * Creates an API client whose methods resolve the response body and throw an `HttpError` on
 * failure. `client` is the raw openapi-fetch client, for the methods one API package adds.
 */
export const createApiClient = <Paths extends ApiPaths>({
    baseUrl,
    getToken,
    middleware = [],
    send = sendWithGlobalFetch
}: ApiClientConfig): { client: Client<Paths>; methods: ThrowingMethods<Paths> } => {
    const client = createClient<Paths>({ baseUrl: resolveBaseUrl(baseUrl), fetch: send })
    const authMiddleware = getToken ? [createAuthMiddleware(getToken)] : []
    // openapi-fetch runs `onError` in reverse order, so the network mapping sees the error last.
    client.use(toNetworkError, rejectBlankPathParams, ...authMiddleware, ...middleware)

    return { client, methods: withThrowingMethods(client) }
}
